// Code referenced from confluence/next/packages/monitoring/src/error-attributes.ts

import { getTraceIdFromResponse } from '../trace-id';

import { BadStatusError } from './bad-status-error';

type ReactErrorAttributes = {
	componentStack: string;
	errorHashCode: string;
};

const REACT_ERROR_CONTEXT_SYMBOL = Symbol('React error context');

export const getReactErrorAttributes: (error: Error | null) => ReactErrorAttributes | undefined = (
	error: Error | null,
): ReactErrorAttributes | undefined => {
	if (typeof error === 'object' && error !== null) {
		return error[REACT_ERROR_CONTEXT_SYMBOL];
	}
	return undefined;
};

type GraphqlErrorAttributes = {
	operationName: string;
	traceId?: string | null;
	statusCode?: number;
	graphqlPath?: string;
};

const GRAPHQL_ERROR_CONTEXT_SYMBOL = Symbol('Graphql error context');

const getGraphqlErrorAttributes = (error: Error | null): GraphqlErrorAttributes | undefined => {
	if (typeof error !== 'object' || error === null) {
		return undefined;
	}

	if (error[GRAPHQL_ERROR_CONTEXT_SYMBOL]) {
		return error[GRAPHQL_ERROR_CONTEXT_SYMBOL];
	}
};

type SlaErrorAttributes = {
	failedSlaExperience: string;
	experienceId: string;
};

const SLA_ERROR_CONTEXT_SYMBOL = Symbol('SLA error context');

const getSlaErrorAttributes = (error: Error | null): SlaErrorAttributes | undefined => {
	if (typeof error === 'object' && error !== null) {
		return error[SLA_ERROR_CONTEXT_SYMBOL];
	}
	return undefined;
};

type BadStatusErrorAttributes = {
	statusCode: number;
	traceId?: string | null;
};

const getBadStatusErrorAttributes = (error: unknown): BadStatusErrorAttributes | undefined => {
	if (!(error instanceof BadStatusError)) {
		return undefined;
	}

	return {
		statusCode: error.response.status,
		traceId: getTraceIdFromResponse(error.response),
	};
};

type ContentErrorAttributes = {
	objectId?: string | null;
	containerId?: string | null;
};

const CONTENT_ERROR_CONTEXT_SYMBOL = Symbol('Content error context');

export function getContentErrorAttributes(error: Error | null): ContentErrorAttributes | undefined {
	if (typeof error === 'object' && error !== null) {
		return error[CONTENT_ERROR_CONTEXT_SYMBOL];
	}
	return undefined;
}

type LoadableErrorAttributes = {
	loadableIds?: string[] | null;
};

const LOADABLE_ERROR_CONTEXT_SYMBOL = Symbol('Loadable error context');

const getLoadableErrorAttributes = (error: Error | null): LoadableErrorAttributes | undefined => {
	if (typeof error === 'object' && error !== null) {
		return error[LOADABLE_ERROR_CONTEXT_SYMBOL];
	}
	return undefined;
};

type LogSafeErrorAttributes = Partial<
	ReactErrorAttributes &
		GraphqlErrorAttributes &
		SlaErrorAttributes &
		BadStatusErrorAttributes &
		ContentErrorAttributes
>;

export const getLogSafeErrorAttributes: (error: unknown) => LogSafeErrorAttributes = (
	error: unknown,
): LogSafeErrorAttributes => {
	if (!error || typeof error !== 'object') {
		return {};
	}

	return {
		...getReactErrorAttributes(error as Error),
		...getGraphqlErrorAttributes(error as Error),
		...getSlaErrorAttributes(error as Error),
		...getContentErrorAttributes(error as Error),
		...getLoadableErrorAttributes(error as Error),
		...getBadStatusErrorAttributes(error),
	};
};

declare global {
	interface Error {
		[GRAPHQL_ERROR_CONTEXT_SYMBOL]?: GraphqlErrorAttributes;
		[REACT_ERROR_CONTEXT_SYMBOL]?: ReactErrorAttributes;
		[SLA_ERROR_CONTEXT_SYMBOL]?: SlaErrorAttributes;
		[CONTENT_ERROR_CONTEXT_SYMBOL]?: ContentErrorAttributes;
		[LOADABLE_ERROR_CONTEXT_SYMBOL]?: LoadableErrorAttributes;
	}
}
